/**
 * 日列を取り合うものを「箱」に分けて並べる。
 *
 * ボードのバーと勤務帯の幅は日単位なので、同じ日列を使うものどうしは矩形が重なる。
 * そのままだと先に描いたほうが完全に隠れ、掴むことも押すこともできなくなる。
 * 日を共有するものを数えて列をその数だけ割り、左から順に箱を割り当てる。
 *
 * Why not レーンをさらに分ける: レーンの単位（トラック × ドライバー）と分割の判定
 * （実時刻の重なり）はユーザー判断で決まっている。同じ日に順にこなす 2 運行は
 * 同じレーンに置いたまま、描画だけ並べる。
 */

/** 日カラムの占有範囲（両端を含む） */
export type DaySpan = { startDayIndex: number; endDayIndex: number };

/** 列を `divisions` 個に割ったときの左から `slot` 番目 */
export type DayBox = { slot: number; divisions: number };

/** 1 日カラムの中で使える範囲（px）。`offset` は列の左端からの距離 */
export type ColumnWindow = { offset: number; width: number };

export const FULL_COLUMN = (dayColWidth: number): ColumnWindow => ({ offset: 0, width: dayColWidth });

/**
 * 箱の中をさらに割る。勤務で割った箱の中を運行で割る（2 段目）ときに使う。
 */
export const windowOf = (outer: ColumnWindow, box: DayBox): ColumnWindow => ({
  offset: outer.offset + (box.slot * outer.width) / box.divisions,
  width: outer.width / box.divisions,
});

/**
 * 箱の矩形。左端は開始日カラムの窓の左、右端は終了日カラムの窓の右。
 * 日カラムをまたぐ間は窓の幅ぶんだけ横にずれた帯になる。
 */
export const boxRect = (
  span: DaySpan,
  win: ColumnWindow,
  dayColWidth: number,
): { left: number; width: number } => {
  // 降が積より前になる壊れたデータでも幅を 0 以下にしない（バーが消えて操作できなくなる）
  const endDayIndex = Math.max(span.startDayIndex, span.endDayIndex);
  return {
    left: span.startDayIndex * dayColWidth + win.offset,
    width: (endDayIndex - span.startDayIndex) * dayColWidth + win.width,
  };
};

/** 両端を含む区間として交差するか。終了日 = 次の開始日 は同じ列を描くので交差とみなす */
const intersects = (a: DaySpan, b: DaySpan) =>
  a.startDayIndex <= b.endDayIndex && b.startDayIndex <= a.endDayIndex;

/**
 * 日を共有するものに箱を割り当てる。
 *
 * 交差でつながった集まり（クラスタ）ごとに数えるので、無関係なものは全幅のまま。
 * 開始日の早い順に空いている箱へ入れる貪欲法で、日の範囲は区間なので必要な箱の数は
 * 最大同時本数に一致する（区間グラフの彩色）。
 *
 * @returns 入力の要素をキーにした箱。要素は参照で引くので、同じ値でも別物として扱える
 */
export function buildDayBoxes<T extends DaySpan>(items: readonly T[]): Map<T, DayBox> {
  const result = new Map<T, DayBox>();
  if (items.length === 0) return result;

  const sorted = [...items].sort(
    (a, b) => a.startDayIndex - b.startDayIndex || a.endDayIndex - b.endDayIndex,
  );

  // クラスタ = 交差でつながった集まり。開始日順に見て、直前までの最大終了日より
  // 後から始まるものは、どれとも交差しないので新しいクラスタになる
  const clusters: T[][] = [];
  let current: T[] = [];
  let clusterEnd = -Infinity;
  for (const item of sorted) {
    if (current.length > 0 && item.startDayIndex > clusterEnd) {
      clusters.push(current);
      current = [];
      clusterEnd = -Infinity;
    }
    current.push(item);
    clusterEnd = Math.max(clusterEnd, Math.max(item.startDayIndex, item.endDayIndex));
  }
  if (current.length > 0) clusters.push(current);

  for (const cluster of clusters) {
    // slotEnds[i] = その箱に入っている最後のものの終了日
    const slotEnds: number[] = [];
    const slotOf = new Map<T, number>();
    for (const item of cluster) {
      const free = slotEnds.findIndex((end) => end < item.startDayIndex);
      const slot = free >= 0 ? free : slotEnds.length;
      slotEnds[slot] = Math.max(item.startDayIndex, item.endDayIndex);
      slotOf.set(item, slot);
    }
    const divisions = slotEnds.length;
    for (const item of cluster) {
      result.set(item, { slot: slotOf.get(item) ?? 0, divisions });
    }
  }

  return result;
}

/** 同じクラスタかどうかを外から知りたいとき用（テスト・デバッグ） */
export const daySpansIntersect = intersects;
